import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";

const Confetti = () => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvas.offsetWidth * dpr;
    canvas.height = canvas.offsetHeight * dpr;

    const colors = ["#cdfb2d", "#f5f0e4", "#ffffff"];
    const parts = Array.from({ length: 80 }, () => ({
      x: canvas.width / 2 + (Math.random() - 0.5) * canvas.width * 0.3,
      y: canvas.height * 0.3,
      vx: (Math.random() - 0.5) * 12 * dpr,
      vy: (Math.random() * -10 - 3) * dpr,
      s: (Math.random() * 5 + 3) * dpr,
      c: colors[(Math.random() * colors.length) | 0],
      r: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.2,
      o: 1,
    }));

    let raf;
    const tick = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      let alive = false;
      for (const p of parts) {
        p.vy += 0.22 * dpr;
        p.x += p.vx;
        p.y += p.vy;
        p.r += p.vr;
        if (p.y > canvas.height * 0.85) p.o -= 0.05;
        if (p.o <= 0) continue;
        alive = true;
        ctx.save();
        ctx.globalAlpha = Math.max(p.o, 0);
        ctx.translate(p.x, p.y);
        ctx.rotate(p.r);
        ctx.fillStyle = p.c;
        ctx.fillRect(-p.s / 2, -p.s / 4, p.s, p.s / 2);
        ctx.restore();
      }
      if (alive) raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, []);

  return <canvas ref={ref} className="pointer-events-none absolute inset-0 h-full w-full" />;
};

const Thanks = () => (
  <div data-testid="thanks-page" className="grain relative flex min-h-screen items-center justify-center overflow-hidden bg-ink px-5 py-16 text-cream">
    <Confetti />
    <div className="relative mx-auto max-w-xl text-center">
      <p className="reveal reveal-1 font-condensed text-sm font-semibold uppercase tracking-[0.35em] text-lime">
        Alignment · Human Design for Business
      </p>
      <h1 className="reveal reveal-2 mt-6 font-condensed text-6xl font-bold uppercase leading-[0.95] tracking-tight sm:text-8xl">
        You're <span className="text-lime">in.</span>
      </h1>
      <p className="reveal reveal-3 mt-8 font-serif-accent text-2xl italic leading-snug text-cream/90 sm:text-3xl">
        "Welcome. You just chose to build your business like the person actually running it — you.
        That alone changes things."
      </p>
      <p className="reveal reveal-3 mt-4 text-xs font-semibold uppercase tracking-[0.3em] text-cream/50">
        — Dasha Love
      </p>

      <div className="reveal reveal-4 mt-10 border border-cream/15 bg-ink-soft p-6 text-left">
        <p className="flex items-center gap-2 font-condensed text-sm font-semibold uppercase tracking-[0.25em] text-lime">
          <Mail className="h-4 w-4" />
          What happens next
        </p>
        <p className="mt-3 text-sm leading-relaxed text-cream/70">
          Check your inbox — your instant access details are on their way to the email you used at
          checkout. If nothing arrives within a few minutes, check spam or reply to your Stripe
          receipt and we'll get you sorted.
        </p>
      </div>

      <Link
        data-testid="thanks-back-link"
        to="/"
        className="reveal reveal-4 mt-8 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-cream/50 transition-colors hover:text-lime"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to the page
      </Link>
    </div>
  </div>
);

export default Thanks;
